/** Util functions */
var PGSDKUtil;
(function (PGSDKUtil) {
  /**
   * @returns the object encoded by the json string provided,
   * or the json parameter itself if parsing fails.
   * This is handy to use when we aren't sure if something is a JSON string or an object already.
   */
  PGSDKUtil.safeParseJson = (json) => {
    try {
      return JSON.parse(json);
    } catch (e) {
      return json;
    }
  };
  /** Logs an error that a deprecated function has been invoked. */
  PGSDKUtil.logDeprecated = (functionName) => {
    console.error(
      `PGSDK invoked function '${functionName}' has been deprecated. It will do nothing.`
    );
  };
})(PGSDKUtil || (PGSDKUtil = {}));
/** Represents the type of message being sent / transmitted from the PGSDK towards the host */
var PGSDKTransmittableAction;
(function (PGSDKTransmittableAction) {
  /**
   * The PGSDK notifies the host that the game has been resized.
   * The params provided will provide the new height and width after the resize.
   */
  PGSDKTransmittableAction["GameResize"] = "GAMERESIZE";
  /** The PGSDK notifies the host that the game has started */
  PGSDKTransmittableAction["GameStarted"] = "GAMESTARTED";
  /** The PGSDK notifies the host that the game has completed */
  PGSDKTransmittableAction["GameCompleted"] = "GAMECOMPLETED";
  /** The PGSDK notifies the host that the game has been aborted */
  PGSDKTransmittableAction["GameAborted"] = "GAMEABORTED";
  /** The PGSDK notifies the host that it is time for an advertising break */
  PGSDKTransmittableAction["GameAdBreak"] = "GAMEADBREAK";
  /** The PGSDK notifies the host that the game has been paused */
  PGSDKTransmittableAction["GamePaused"] = "GAMEPAUSED";
  /** The PGSDK provides parameters to the host that the host can use to track various game data */
  PGSDKTransmittableAction["GameTrack"] = "GAMETRACK";
  /** The PGSDK requests that the host refresh the game */
  PGSDKTransmittableAction["GameRefresh"] = "GAMEREFRESH";
  /**
   * The PGSDK requests that the host opens the link provided in the provided parameters.
   * The url to open must be provided as a parameter.
   * This url may be any kind of link, both internal to the domain of the host, and external.
   * It the responsibility of the host to determine how best to handle the link.
   */
  PGSDKTransmittableAction["OpenLink"] = "OPENLINK";
  /**
   * The PGSDK requests that the host share the game, using the provided parameters.
   * This may be done, for example, for the purpose of allowing the user to brag about their highscore.
   * It is up to the host to determine what 'sharing' means.
   * The provided parameters define the data that the game requests the host include when sharing the game information.
   */
  PGSDKTransmittableAction["Share"] = "SHARE";
  /**
   * The PGSDK requests that the host share the game.
   * Does not provide the share parameters, for legacy reasons.
   * This event is only sent to ensure backwards compatibility with legacy hosts who cannot handle the modern share event.
   * So this event is also sent whenever the modern event is sent,
   * but the host should take care to only handle one of them, as they represent the same event.
   * The modern event is:
   * @see Share
   */
  PGSDKTransmittableAction["LegacyShare"] = "GAMESHARE";
  /**
   * The PGSDK provides the current PGSDK version to the host,
   * as the response to a previously recieved `PGSDKReceivableAction.PgsdkVersionRequest`
   */
  PGSDKTransmittableAction["PgsdkVersionResponse"] = "PGSDKVERSIONRESPONSE";
})(PGSDKTransmittableAction || (PGSDKTransmittableAction = {}));
/** Represents the type of message being recieved by the PGSDK from the host */
var PGSDKReceivableAction;
(function (PGSDKReceivableAction) {
  /** The host requests that the PGSDK initialises the game */
  PGSDKReceivableAction["InvokeStartParams"] = "INVOKESTARTPARAMS";
  /** The host notifies the PGSDK that the advertising break has ended */
  PGSDKReceivableAction["AdBreakEnded"] = "ADBREAKENDED";
  /** The host notifies the PGSDK that the game is running in App mode */
  PGSDKReceivableAction["IsApp"] = "ISAPP";
  /** The host notifies the PGSDK that the game is running in Web mode */
  PGSDKReceivableAction["IsWeb"] = "ISWEB";
  /** The host notifies the PGSDK that the game has been resumed */
  PGSDKReceivableAction["Resume"] = "RESUME";
  /** The host sends a test event to the PGSDK */
  PGSDKReceivableAction["Test"] = "TEST";
  /**
   * The host requests the current version of this PGSDK.
   * PGSDK should respond by sending a `PGSDKTransmittableAction.PgsdkVersionResponse`
   */
  PGSDKReceivableAction["PgsdkVersionRequest"] = "PGSDKVERSIONREQUEST";
})(PGSDKReceivableAction || (PGSDKReceivableAction = {}));
/**
 * Identifies in which platform the PGSDK is active.
 */
var PlatformState;
(function (PlatformState) {
  PlatformState[(PlatformState["App"] = 0)] = "App";
  PlatformState[(PlatformState["Web"] = 1)] = "Web";
})(PlatformState || (PlatformState = {}));
const PGSDKVersionName = "3.0.0";
// Reference paths tell the TS compiler the order in which to compile our source files
// This file has dependencies on the following files
/// <reference path="util.ts" />
/// <reference path="model.ts" />
/// <reference path="version.ts" />
/** This class defines the API of the PGSDK. It defines all the functionality of this SDK. */
class PGSDKApi {
  /** App mode toggle - used by the game publisher to alternate behaviour per platform */
  get isApp() {
    return this.platformState === PlatformState.App;
  }
  /** Web mode toggle - used by the game publisher to alternate behaviour per platform */
  get isWeb() {
    return this.platformState === PlatformState.Web;
  }
  constructor() {
    /** Debug mode toggle - used to toggle whether to show debug logs */
    this.isDebug = false;
    /** Web is the default assumption. */
    this.platformState = PlatformState.Web;
    this.context = {
      adBreakEndedHandler: null,
      startHandler: null,
      resumeHandler: null,
      testHandler: null,
      startParams: null,
      hasStarted: false,
      hasSetupResizeObserver: false,
    };
    /**
     * The current version of the PGSDK.
     * Public for games to read directly.
     */
    this.pgsdkVersion = PGSDKVersionName;
    /**
     * Set the startParams that will be passed to the game when it starts.
     * Will successfully start the game if the startHandler is already set by having previously called `onStart`.
     */
    this.initGame = (startParams) => {
      this.context.startParams = startParams;

      this.attemptStart();
      this.initResizer();
     
    };
    /**
     * Invokes the testHandler if one is set, and passes it the provided `testParams`
     */
    this.testGame = (testParams) => {
      var _a, _b;
      this.debugLog("TEST", testParams);
      (_b = (_a = this.context).testHandler) === null || _b === void 0
        ? void 0
        : _b.call(_a, testParams);
    };
    /**
     * Enables or disables debug mode depending on the provided parameter.
     * Debug mode is used for logging debug information
     */
    this.setDebugEnabled = (isDebugMode) => {
      this.isDebug = isDebugMode;
    };
    /**
     * Registers the provided start handler, and optionally also the test handler, if provided.
     * Will successfully start the game if the startParams are already set by having previously called `initGame`.
     * @see PGSDKContext.startHandler
     * @see PGSDKContext.testHandler
     */
    this.onStart = (startHandler, testHandler) => {
      this.context.startHandler = startHandler;
      this.attemptStart();
      this.initResizer();
      if (testHandler != null) {
        this.context.testHandler = testHandler;
      }
    };
    /**
     * Registers the onAdBreakEnded handler.
     * @see PGSDKContext.adBreakEndedHandler
     */
    this.onAdBreakEnded = (adBreakEndedHandler) => {
      this.context.adBreakEndedHandler = adBreakEndedHandler;
    };
    /**
     * Registers the onResume handler.
     * @see PGSDKContext.resumeHandler
     */
    this.onResume = (resumeHandler) => {
      this.context.resumeHandler = resumeHandler;
    };
    /**
     * Attempts to setup the resize observer for the body of the game.
     * When setup successfully, this will communicate GameResize events to the host.
     * For this to setup successfully there must be a `body` element in the document.
     */
    this.initResizer = () => {
      // Don't setup the observer multiple times
      if (this.context.hasSetupResizeObserver) return;
      const bodyType = "body";
      const body = document.querySelector(bodyType);
      if (body === null) {
        console.error(
          `PGSDK cannot setup resize-observer - document contains no element of type ${bodyType}`
        );
        return;
      }
      const observer = () => {
        const params = {
          height: body.clientHeight,
          width: body.clientWidth,
        };
        this.sendMessage(PGSDKTransmittableAction.GameResize, params);
      };
      const resizeObserver = new ResizeObserver(observer);
      resizeObserver.observe(body);
      this.context.hasSetupResizeObserver = true;
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.GameStarted
     */
    this.gameStarted = () => {
      this.sendMessage(PGSDKTransmittableAction.GameStarted);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.Share
     * @see PGSDKTransmittableAction.LegacyShare
     */
    this.gameShare = (params) => {
      const text = params.text;
      // The params value uses dynamic object keys which the host finds it difficult to parse.
      // So rework the params into a format that the host can understand more easily.
      const config = Object.assign(Object.assign({}, params), {
        text: undefined,
      });
      const sharePayload = {
        text,
        config,
      };
      this.sendMessage(PGSDKTransmittableAction.Share, sharePayload);
      // Send legacy share message as well, for legacy hosts who cannot handle the new event
      const legacyPayload = params;
      this.sendMessage(PGSDKTransmittableAction.LegacyShare, legacyPayload);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.GameTrack
     */
    this.gameTrack = (params) => {
      this.sendMessage(PGSDKTransmittableAction.GameTrack, params);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.GameCompleted
     */
    this.gameCompleted = (params) => {
      this.sendMessage(PGSDKTransmittableAction.GameCompleted, params);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.GameAborted
     */
    this.gameAborted = (params) => {
      this.sendMessage(PGSDKTransmittableAction.GameAborted, params);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.GameAdBreak
     */
    this.gameAdBreak = (params) => {
      this.sendMessage(PGSDKTransmittableAction.GameAdBreak, params);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.GamePaused
     */
    this.gamePaused = (params) => {
      this.sendMessage(PGSDKTransmittableAction.GamePaused, params);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.GameRefresh
     */
    this.gameRefresh = (params) => {
      this.sendMessage(PGSDKTransmittableAction.GameRefresh, params);
    };
    /**
     * Sends a message to the host. This message is documented at:
     * @see PGSDKTransmittableAction.OpenLink
     */
    this.openLink = (url) => {
      const params = { url };
      this.sendMessage(PGSDKTransmittableAction.OpenLink, params);
    };
    /* Deprecated functions */
    /**
     * @deprecated use setDebugMode instead.
     * This function is deprecated because it takes a value parameter which is never used -
     * it would always enable debug mode, with no way to disable it.
     */
    this.enableDebugMode = (value) => {
      PGSDKUtil.logDeprecated("enableDebugMode");
    };
    /**
     * @deprecated This function has been removed as it is not the responsibility of the PGSDK.
     */
    this.stripMyPoiWorldJSON = (obj, parent, name) => {
      PGSDKUtil.logDeprecated("stripMyPoiWorldJSON");
    };
    /**
     * @deprecated This function has been removed as it is not the responsibility of the PGSDK.
     */
    this.getPuzzleByTitle = (obj, title) => {
      PGSDKUtil.logDeprecated("getPuzzleByTitle");
    };
    /**
     * @deprecated This function has been removed as it is not the responsibility of the PGSDK.
     */
    this.xmlToJson = (xml) => {
      PGSDKUtil.logDeprecated("xmlToJson");
    };
    /**
     * @deprecated use openLink instead.
     * This functionality has been replaced with openLink to provide a consistent way to open any kind of link within the host,
     * regardless of whether it is an article, an internal link, or an external link - the host will handle it.
     *
     * The params will be of the following form: `{ id: 42, url: 'https://hln.be/...' }`
     */
    this.gotoArticle = (params) => {
      PGSDKUtil.logDeprecated("gotoArticle");
    };
    /**
     * @deprecated use openLink instead.
     * This functionality has been replaced with openLink to provide a consistent way to open any kind of link within the host,
     * regardless of whether it is an article, an internal link, or an external link - the host will handle it.
     */
    this.openExternalLink = (params) => {
      PGSDKUtil.logDeprecated("openExternalLink");
    };
    /* Private functions */
    /**
     * Try to start the game by invoking the startHandler with the start parameters.
     * For this to succeed, the following conditions must be met:
     * - startHandler must have been set by previously calling `onStart`
     * - startParams must have been set by previously calling `initGame`
     *
     * Additionally, the startHandler will be invoked at most once in the lifetime of the game -
     * attempting to invoke the startHandler a second time after it has already successfully been invoked will fail.
     */
    this.attemptStart = () => {
      const startHandler = this.context.startHandler;
      const startParams = this.context.startParams;
      if (
        this.context.hasStarted ||
        startHandler == null ||
        startParams == null
      )
        return;
      this.context.hasStarted = true;
      startHandler(startParams);
    };
    /** Logs the provided arguments only if currently operating in debug mode */
    this.debugLog = (...data) => {
      if (this.isDebug) console.log("PGSDK:", ...data);
    };
    /**
     * Sends a message to the host,
     * for example to the app in which the game is playing.
     *
     * The message sent will be an object containing everything in the 'params' argument,
     * with an additional 'action' field,
     * which will have the value of the provided 'action' parameter.
     */
    this.sendMessage = (action, params) => {
      var _a, _b, _c;
      // Prepare data - it's everything in params, with some additional fields
      const data = Object.assign(
        { action },
        params !== null && params !== void 0 ? params : {}
      );
      const dataString = JSON.stringify(data);
      this.debugLog("Sending message", dataString);
      const targetWindow = window.parent;
      // Handle communication with hosts
      // Post message with "*" because we don't know or care what the target origin is
      // Without this, certain browsers (Chrome) will block messages as a security feature
      const targetHost = "*";
      // Try sending through the window directly.
      // This is for browser hosts, and also for backwards compatibility.
      // App hosts should not be listening to these messages, to avoid duplicate messages -
      // app hosts have their own message posting mechanism below.
      (_a =
        targetWindow === null || targetWindow === void 0
          ? void 0
          : targetWindow.postMessage) === null || _a === void 0
        ? void 0
        : _a.call(targetWindow, dataString, targetHost);
      // Try sending through the app host window - only for apps
      // Use the window as any - we'll make sure the values exist
      const appHostWindow = targetWindow;
      // If the host is an app, it will have created a communication channel for the PGSDK to send events through.
      // If the host is a browser, this channel simply won't exist, so nothing will be sent - no harm done.
      // This channel is named 'ReactNativeWebView' for legacy reasons - it does NOT imply that the host is running ReactNative.
      try {
        // In certain situations, attempting to read the `ReactNativeWebView` of the window will throw a `DOMException`.
        // This happens when ALL the following conditions are met:
        // - the appHostWindow is cross-origin - for example an iframe displaying a local file
        // - the browser running the game protects against cross-origin frame access - for example, Chrome with default settings
        // This exception is therefore to be expected -
        // in these situations, the host should be observing the message using the web-host message system (above),
        // so they should not miss any message.
        (_c =
          (_b =
            appHostWindow === null || appHostWindow === void 0
              ? void 0
              : appHostWindow.ReactNativeWebView) === null || _b === void 0
            ? void 0
            : _b.postMessage) === null || _c === void 0
          ? void 0
          : _c.call(_b, dataString, targetHost);
      } catch (e) {
        this.debugLog(
          "Failed to send message to app host, this is to be expected if the window is a cross-origin iframe"
        );
      }
    };
    /** Invoked when the PGSDK receives a message from the host */
    this.receiveMessage = (message) => {
      var _a, _b, _c, _d, _e, _f, _g;
      this.debugLog("Received message", message);
      const rawData =
        message === null || message === void 0 ? void 0 : message.data;
      if (rawData === null || rawData === undefined) {
        return;
      }
      const data =
        (_a = PGSDKUtil.safeParseJson(rawData)) !== null && _a !== void 0
          ? _a
          : {};
      const action = data === null || data === void 0 ? void 0 : data.action;
      switch (action) {
        case PGSDKReceivableAction.InvokeStartParams: {
          // Remove the action field - the startParams don't need it
          const params = Object.assign(Object.assign({}, data), {
            action: undefined,
          });

          this.initGame(params);
         
          break;
        }
        case PGSDKReceivableAction.AdBreakEnded: {
          (_c = (_b = this.context).adBreakEndedHandler) === null ||
          _c === void 0
            ? void 0
            : _c.call(_b, data);
          break;
        }
        case PGSDKReceivableAction.IsApp: {
          this.platformState = PlatformState.App;
          break;
        }
        case PGSDKReceivableAction.IsWeb: {
          this.platformState = PlatformState.Web;
          break;
        }
        case PGSDKReceivableAction.Resume: {
          (_e = (_d = this.context).resumeHandler) === null || _e === void 0
            ? void 0
            : _e.call(_d, data);
          break;
        }
        case PGSDKReceivableAction.Test: {
          (_g = (_f = this.context).testHandler) === null || _g === void 0
            ? void 0
            : _g.call(_f, data);
          break;
        }
        case PGSDKReceivableAction.PgsdkVersionRequest:
          {
            const params = { pgsdkVersion: this.pgsdkVersion };
            this.sendMessage(
              PGSDKTransmittableAction.PgsdkVersionResponse,
              params
            );
          }
          break;
        default:
      }
    };
    /** Register listeners for messages being sent from the host to the PGSDK */
    this.registerHostMessageListeners = () => {
      // Subscribe for Android post messages
      document.addEventListener("message", this.receiveMessage);
      // Subscribe for iOS and Web post messages
      window.addEventListener("message", this.receiveMessage);
    };
    this.registerHostMessageListeners();
  }
}
/// <reference path="api.ts" />
/**
 * The PGSDK instance.
 * This is the only export intended for public use by consumers of this SDK.
 */
const PGSDK = new PGSDKApi();

export { PGSDK };
